import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import {Concept} from 'types';
import type {WorkSummary} from 'api/types';
import type {LinkedAgentMultiRole} from 'schemaDefinition/types';
import type {CodeListMap} from 'services/codelists/types';
import type {
  ExpressionV4,
  ManifestationItem,
  WorkMetadata,
  WorkV4,
} from '../types';
import {getMetadataOrder, sortThemaCodes} from '../functions';

export const mapWorkMetadataToWorkSummary = (
  metadata: WorkMetadata,
  codelists: CodeListMap,
): WorkSummary => {
  const order = getMetadataOrder(
    {...metadata, changeRequests: []},
    [],
    codelists,
  );

  // Split OtherTitles into separate fields for easier rendering in tables.
  const otherTitleType36 = metadata.work?.otherTitles
    ?.filter(t => t.type === '36')
    ?.map(t => t.value);
  const otherTitleType37 = metadata.work?.otherTitles
    ?.filter(t => t.type === '37')
    ?.map(t => t.value);

  const findManifestaion = (id: string) =>
    metadata.manifestations.find(m => m.id === id);

  const allManifestationItems = order.flatMap(o =>
    o.content.filter(c => c.type === Concept.manifestation),
  ) as ManifestationItem[];
  const manifestationItemWithTitle = allManifestationItems.find(
    item => (findManifestaion(item.id)?.mainTitles?.length ?? 0) > 0,
  );
  const mainTitles = manifestationItemWithTitle
    ? findManifestaion(manifestationItemWithTitle.id)?.mainTitles
    : undefined;

  const agents = mergeAgentsById(metadata.work, metadata.expressions);

  const publishedYear = metadata.manifestations
    .map(m => m.publishedYear)
    .filter((y): y is number => y !== undefined);

  return {
    ...metadata.work,
    agents,
    mainTitles: mainTitles ?? null,
    themaCodes: sortThemaCodes(metadata.work.themes),
    themaLabels: sortThemaCodes(metadata.work.themes),
    otherTitleType36,
    otherTitleType37,
    publishedYear,
  };
};

const mergeAgentsById = (
  work: WorkV4,
  expressions: ExpressionV4[],
): LinkedAgentMultiRole[] | undefined => {
  const all = [
    ...(work.agents ?? []),
    ...expressions.map(e => e.agents ?? []).flat(),
  ];
  const groupedById = groupBy(all, a => a.link.entityId);
  const mergedOnId = Object.keys(groupedById).map<LinkedAgentMultiRole>(
    key => ({
      ...groupedById[key][0],
      roles: uniq(groupedById[key].flatMap(summary => summary.roles)),
    }),
  );

  return mergedOnId;
};
